import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../index"; // Import Firestore instance
import "./DeckView.css";

const CACHE_KEY = "cardListCache";
const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours

const fetchCardData = async () => {
  try {
    // Check for cached data
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (cachedData) {
      const { timestamp, data } = JSON.parse(cachedData);
      if (Date.now() - timestamp < CACHE_TTL) {
        console.log("Using cached card data.");
        return data;
      }
    }

    console.log("Fetching card data from Firebase.");
    const cardCollection = collection(db, "cards");
    const cardSnapshot = await getDocs(cardCollection);

    const cardList = cardSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Cache the data
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ timestamp: Date.now(), data: cardList })
    );

    return cardList;
  } catch (error) {
    console.error("Error fetching card data:", error);
    return [];
  }
};

const DeckView = () => {
  const { archetype } = useParams(); // Get archetype from URL
  const [sortedCards, setSortedCards] = useState(null);
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeckData = async () => {
      try {
        const tournaments = await fetch("/tournaments.json").then((res) =>
          res.json()
        );
        const cards = await fetchCardData();

        const filteredDecks = tournaments
          .flatMap((tournament) => {
            if (!Array.isArray(tournament.decklists)) {
              console.error(
                `Missing or invalid decklists in tournament:`,
                tournament
              );
              return [];
            }

            return tournament.decklists.map((deck, index) => ({
              ...deck,
              tournament: tournament.name || "Unknown Tournament",
              date: tournament.date || "1970-01-01",
              placement: index + 1,
            }));
          })
          .filter((deck) => deck.archetype === archetype);

        const aggregatedCards = { oshi: [], main: [], cheers: [] };

        filteredDecks.forEach((deck) => {
          const uniqueCardIds = new Set();

          if (deck.oshi) {
            const [cardId] = deck.oshi.split("_");
            if (!uniqueCardIds.has(cardId)) {
              uniqueCardIds.add(cardId);
              const card = cards.find((c) => c.id === cardId);
              if (card) {
                aggregatedCards.oshi.push({ ...card, count: 1 });
              }
            }
          }

          ["main", "cheers"].forEach((key) => {
            deck[key]?.forEach((cardEntry) => {
              const [rawCardId, quantity] = Object.entries(cardEntry)[0];
              const [cardId] = rawCardId.split("_");
              if (!uniqueCardIds.has(cardId)) {
                uniqueCardIds.add(cardId);
                const card = cards.find((c) => c.id === cardId);
                if (card) {
                  aggregatedCards[key].push({ ...card, count: quantity });
                }
              }
            });
          });
        });

        const computeCardStats = (cards, totalDecks) => {
          const cardMap = {};
          cards.forEach((card) => {
            if (!cardMap[card.id]) {
              cardMap[card.id] = { ...card, total: 0, uniqueDecks: 0 };
            }
            cardMap[card.id].total += card.count;
            cardMap[card.id].uniqueDecks++;
          });

          return Object.values(cardMap).map((card) => ({
            ...card,
            average: card.total / card.uniqueDecks,
            percentage: ((card.uniqueDecks / totalDecks) * 100).toFixed(2),
          }));
        };

        const totalDecks = filteredDecks.length;

        setSortedCards({
          oshi: computeCardStats(aggregatedCards.oshi, totalDecks),
          main: computeCardStats(aggregatedCards.main, totalDecks),
          cheers: computeCardStats(aggregatedCards.cheers, totalDecks),
        });

        const results = filteredDecks
          .filter((deck) => new Date(deck.date).toString() !== "Invalid Date")
          .map((deck) => ({
            date: new Date(deck.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            tournament: deck.tournament,
            placement: deck.placement,
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 15);

        setRecentResults(results);
      } catch (error) {
        console.error("Error fetching deck data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeckData();
  }, [archetype]);

  if (loading) return <div>Loading...</div>;
  if (
    !sortedCards ||
    (!sortedCards.oshi.length &&
      !sortedCards.main.length &&
      !sortedCards.cheers.length)
  ) {
    return (
      <div className="deck-view-container">
        No data available for this archetype.
      </div>
    );
  }

  return (
    <div className="deck-view-container">
      <h1 className="deck-view-title">{archetype} Deck View</h1>

      {["oshi", "main", "cheers"].map((key) => (
        <div className={`deck-section ${key}-section`} key={key}>
          <h2 className="deck-section-title">
            {key.charAt(0).toUpperCase() + key.slice(1)} Deck
          </h2>
          <div className="deck-list">
            {sortedCards[key].map((card) => (
              <Link
                to={`/card/${card.id}_${card.rarity}`}
                key={`${card.id}_${card.rarity}`}
                className="deck-card-link"
              >
                <div className="deck-card">
                  <img
                    src={`/images/${card.id}_${card.rarity}.png`}
                    alt={card.name}
                    className="deck-card-image"
                    onError={(e) => (e.target.src = "/images/default.png")}
                  />
                  <div className="deck-card-footer">
                    <span className="deck-card-title">{card.name}</span>
                    <span className="deck-card-percentage">
                      {card.percentage}% of decks
                    </span>
                    <span className="deck-card-average">
                      Avg: {card.average.toFixed(2)} per deck
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}

      {recentResults.length > 0 && (
        <div className="deck-section results-section">
          <h2 className="deck-section-title">Recent Results</h2>
          <table className="results-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Tournament</th>
                <th>Placement</th>
              </tr>
            </thead>
            <tbody>
              {recentResults.map((result, index) => (
                <tr key={index}>
                  <td>{result.date}</td>
                  <td>{result.tournament}</td>
                  <td>{result.placement}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DeckView;
